import { useEffect, useMemo, useState } from "react";
import { useAnimals } from "../../../hooks/useAnimals";
import { useCustomers } from "../../../hooks/useCustomers";
import { useUsers } from "../../../hooks/useUsers";
import {
  getDeliveryAssignments,
  rehydrateDeliveryAssignments,
  removeDeliveryAssignment,
  subscribeDeliveryAssignments,
  upsertDeliveryAssignment,
  type DeliveryAssignment,
  type DeliveryStatus,
} from "../../../lib/deliveryStore";
import { exportRowsToCsv, type ExportColumn } from "../../../lib/excelExport";
import {
  getDisplayKupeNo,
  getDisplaySiparisNo,
} from "../../../lib/animalLabels";
import type { AnimalItem, ShareItem } from "../../../mocks/animals";
import type { CustomerItem } from "../../../mocks/customers";

type StatusFilter = "Tümü" | "Atanmadı" | DeliveryStatus;

interface DraftState {
  address: string;
  carrierUserId: string;
  note: string;
}

interface FilterState {
  customer: string;
  phone: string;
  kupeNo: string;
  siparisNo: string;
  status: StatusFilter;
}

interface TransportRow {
  id: string;
  animalId: number;
  shareId: number;
  kupeNo: string;
  siparisNo: string;
  tur: AnimalItem["tur"];
  customerId: number | undefined;
  customerName: string;
  customerPhone: string;
  address: string;
  shareNo: string;
  hisseSayisi: number;
  saleStatus: string;
  animalStatus: AnimalItem["durum"];
  kalan: number;
  total: number;
  transportLabel: string;
  delivery?: DeliveryAssignment;
}

interface ExportTransportRow {
  musteri: string;
  telefon: string;
  kupeNo: string;
  siparisNo: string;
  hisse: string;
  tur: string;
  finans: string;
  hayvanDurumu: string;
  tasimaDurumu: string;
  tasiyici: string;
  adres: string;
  not: string;
}

const EMPTY_DRAFT: DraftState = {
  address: "",
  carrierUserId: "",
  note: "",
};

const EMPTY_FILTERS: FilterState = {
  customer: "",
  phone: "",
  kupeNo: "",
  siparisNo: "",
  status: "Tümü",
};

type ShareLike = ShareItem & {
  musteriId?: number;
  tasimaVar?: boolean;
};

function normalizePhone(value?: string) {
  return (value ?? "").replace(/\D/g, "");
}

function normalizeText(value?: string) {
  return (value ?? "").trim().toLocaleLowerCase("tr-TR");
}

function fmt(n: number) {
  return n.toLocaleString("tr-TR", {
    style: "currency",
    currency: "TRY",
    maximumFractionDigits: 0,
  });
}

function getDisplayStatus(row: TransportRow): StatusFilter {
  if (!row.delivery) return "Atanmadı";
  return row.delivery.status;
}

function statusBadge(status: StatusFilter) {
  if (status === "Teslim Edildi") {
    return "bg-farm-green-pale text-farm-green border-farm-green/20";
  }
  if (status === "Taşıma Halinde") {
    return "bg-farm-gold-pale text-farm-gold border-farm-gold/20";
  }
  if (status === "Hazır") {
    return "bg-farm-earth-pale text-farm-earth border-farm-earth/20";
  }
  return "bg-red-50 text-red-500 border-red-200";
}

function financeBadge(kalan: number) {
  return kalan > 0
    ? "bg-red-50 text-red-600 border-red-200"
    : "bg-green-50 text-green-600 border-green-200";
}

function compareText(a: string, b: string) {
  return a.localeCompare(b, "tr", { numeric: true, sensitivity: "base" });
}

function buildDraft(row: TransportRow): DraftState {
  return {
    address: row.delivery?.address ?? row.address ?? "",
    carrierUserId: row.delivery?.carrierUserId
      ? String(row.delivery.carrierUserId)
      : "",
    note: row.delivery?.note ?? "",
  };
}

function findCustomerFromShare(
  customers: CustomerItem[],
  share: ShareLike
): CustomerItem | null {
  if (typeof share.musteriId === "number") {
    const byId = customers.find((customer) => customer.id === share.musteriId);
    if (byId) return byId;
  }

  const byPhone = customers.find(
    (customer) =>
      normalizePhone(customer.telefon) &&
      normalizePhone(customer.telefon) === normalizePhone(share.telefon)
  );
  if (byPhone) return byPhone;

  const byName = customers.find(
    (customer) =>
      normalizeText(customer.adSoyad) === normalizeText(share.musteriAdi)
  );
  if (byName) return byName;

  return null;
}

function getTransportEligibility(
  share: ShareLike,
  delivery?: DeliveryAssignment
) {
  if (delivery) return true;
  if (share.tasimaVar) return true;
  if (Number(share.tasimaUcreti ?? 0) > 0) return true;
  return false;
}

function deriveRows(
  animals: AnimalItem[],
  customers: CustomerItem[],
  deliveries: DeliveryAssignment[]
): TransportRow[] {
  const deliveryMap = new Map(deliveries.map((item) => [item.shareId, item]));
  const rows: TransportRow[] = [];

  for (const animal of animals) {
    for (const rawShare of animal.hisseler ?? []) {
      const share = rawShare as ShareLike;
      const delivery = deliveryMap.get(share.id);

      if (!getTransportEligibility(share, delivery)) continue;

      const customer = findCustomerFromShare(customers, share);

      rows.push({
        id: `${animal.id}-${share.id}`,
        animalId: animal.id,
        shareId: share.id,
        kupeNo: getDisplayKupeNo(animal),
        siparisNo: getDisplaySiparisNo(animal),
        tur: animal.tur,
        customerId: customer?.id,
        customerName: customer?.adSoyad ?? share.musteriAdi ?? "Müşteri bulunamadı",
        customerPhone: customer?.telefon ?? share.telefon ?? "",
        address: delivery?.address ?? customer?.kisaNot?.trim() ?? "",
        shareNo: share.hisseNo,
        hisseSayisi: share.hisseSayisi ?? 1,
        saleStatus: share.durum,
        animalStatus: animal.durum,
        kalan: Number(share.kalan ?? 0),
        total:
          Number(share.tutar ?? 0) +
          Number(share.kesimUcreti ?? 0) +
          Number(share.parcalamaUcreti ?? 0) +
          Number(share.tasimaUcreti ?? 0) -
          Number(share.indirim ?? 0),
        transportLabel: share.tasimaVar ? "Taşıma Var" : "Taşıma Ücreti",
        delivery,
      });
    }
  }

  return rows.sort((a, b) => {
    const order: StatusFilter[] = [
      "Atanmadı",
      "Hazır",
      "Taşıma Halinde",
      "Teslim Edildi",
    ];

    return (
      order.indexOf(getDisplayStatus(a)) - order.indexOf(getDisplayStatus(b)) ||
      compareText(a.customerName, b.customerName) ||
      compareText(a.kupeNo, b.kupeNo) ||
      compareText(a.shareNo, b.shareNo)
    );
  });
}

export default function TasimaPage() {
  const { animals } = useAnimals();
  const { customers } = useCustomers();
  const { users } = useUsers();

  const [deliveryVersion, setDeliveryVersion] = useState(0);
  const [drafts, setDrafts] = useState<Record<string, DraftState>>({});
  const [filters, setFilters] = useState<FilterState>(EMPTY_FILTERS);

  useEffect(() => {
    void rehydrateDeliveryAssignments();

    return subscribeDeliveryAssignments(() => {
      setDeliveryVersion((value) => value + 1);
    });
  }, []);

  const carrierUsers = useMemo(
    () => users.filter((user) => user.rol === "Taşıyıcı" && user.durum === "Aktif"),
    [users]
  );

  const carrierMap = useMemo(
    () => new Map(carrierUsers.map((user) => [user.id, user.adSoyad])),
    [carrierUsers]
  );

  const baseRows = useMemo(
    () => deriveRows(animals, customers, getDeliveryAssignments()),
    [animals, customers, deliveryVersion]
  );

  useEffect(() => {
    setDrafts((prev) => {
      const next: Record<string, DraftState> = {};
      for (const row of baseRows) {
        next[row.id] = prev[row.id] ?? buildDraft(row);
      }
      return next;
    });
  }, [baseRows]);

  const filteredRows = useMemo(() => {
    return baseRows.filter((row) => {
      if (
        filters.customer.trim() &&
        !normalizeText(row.customerName).includes(normalizeText(filters.customer))
      ) {
        return false;
      }

      if (
        filters.phone.trim() &&
        !normalizePhone(row.customerPhone).includes(normalizePhone(filters.phone))
      ) {
        return false;
      }

      if (
        filters.kupeNo.trim() &&
        !normalizeText(row.kupeNo).includes(normalizeText(filters.kupeNo))
      ) {
        return false;
      }

      if (
        filters.siparisNo.trim() &&
        !normalizeText(row.siparisNo).includes(normalizeText(filters.siparisNo))
      ) {
        return false;
      }

      if (filters.status !== "Tümü" && getDisplayStatus(row) !== filters.status) {
        return false;
      }

      return true;
    });
  }, [baseRows, filters]);

  const summary = useMemo(
    () => ({
      toplam: filteredRows.length,
      atanmadi: filteredRows.filter((row) => !row.delivery).length,
      hazir: filteredRows.filter((row) => row.delivery?.status === "Hazır").length,
      yolda: filteredRows.filter((row) => row.delivery?.status === "Taşıma Halinde")
        .length,
      teslim: filteredRows.filter((row) => row.delivery?.status === "Teslim Edildi")
        .length,
    }),
    [filteredRows]
  );

  function updateDraft(rowId: string, patch: Partial<DraftState>) {
    setDrafts((prev) => ({
      ...prev,
      [rowId]: {
        ...(prev[rowId] ?? EMPTY_DRAFT),
        ...patch,
      },
    }));
  }

  function saveAssignment(row: TransportRow) {
    const draft = drafts[row.id] ?? buildDraft(row);
    const carrierUserId = Number(draft.carrierUserId);

    if (!carrierUserId) {
      window.alert("Lütfen bir taşıyıcı seç.");
      return;
    }

    const carrier = carrierUsers.find((item) => item.id === carrierUserId);
    if (!carrier) {
      window.alert("Seçilen taşıyıcı bulunamadı.");
      return;
    }

    const existingStatus = row.delivery?.status ?? "Hazır";

    void upsertDeliveryAssignment({
      id: row.delivery?.id ?? row.id,
      shareId: row.shareId,
      animalId: row.animalId,
      customerId: row.customerId,
      customerName: row.customerName,
      address: draft.address.trim(),
      carrierId: carrier.id,
      carrierUserId: carrier.id,
      carrierName: carrier.adSoyad,
      carrierPhone: carrier.telefon,
      shareNo: row.shareNo,
      note: draft.note.trim(),
      liveLocationUrl: row.delivery?.liveLocationUrl,
      startedAt: row.delivery?.startedAt,
      deliveredAt: row.delivery?.deliveredAt,
      status: existingStatus,
    });
  }

  function clearAssignment(row: TransportRow) {
    const ok = window.confirm(
      `${row.customerName} / ${row.shareNo} için taşıma ataması silinsin mi?`
    );
    if (!ok) return;

    void removeDeliveryAssignment(row.shareId);
  }

  function setStatus(row: TransportRow, status: DeliveryStatus) {
    const draft = drafts[row.id] ?? buildDraft(row);
    const carrierUserId = Number(draft.carrierUserId || row.delivery?.carrierUserId || 0);

    if (!carrierUserId) {
      window.alert("Önce taşıyıcı seçip kaydet.");
      return;
    }

    const carrier = carrierUsers.find((item) => item.id === carrierUserId);
    if (!carrier) {
      window.alert("Seçilen taşıyıcı bulunamadı.");
      return;
    }

    const now = new Date().toISOString();

    void upsertDeliveryAssignment({
      id: row.delivery?.id ?? row.id,
      shareId: row.shareId,
      animalId: row.animalId,
      customerId: row.customerId,
      customerName: row.customerName,
      address: (draft.address || row.address || "").trim(),
      carrierId: carrier.id,
      carrierUserId: carrier.id,
      carrierName: carrier.adSoyad,
      carrierPhone: carrier.telefon,
      shareNo: row.shareNo,
      note: (draft.note || row.delivery?.note || "").trim(),
      liveLocationUrl: row.delivery?.liveLocationUrl,
      status,
      startedAt:
        status === "Hazır"
          ? undefined
          : row.delivery?.startedAt ?? now,
      deliveredAt: status === "Teslim Edildi" ? now : undefined,
    });
  }

  function exportCurrent() {
    const rows: ExportTransportRow[] = filteredRows.map((row) => ({
      musteri: row.customerName,
      telefon: row.customerPhone,
      kupeNo: row.kupeNo,
      siparisNo: row.siparisNo,
      hisse: `${row.shareNo} x${row.hisseSayisi}`,
      tur: row.tur,
      finans: row.kalan > 0 ? `Borçlu (${fmt(row.kalan)})` : "Tamam",
      hayvanDurumu: row.animalStatus,
      tasimaDurumu: getDisplayStatus(row),
      tasiyici: row.delivery?.carrierName ?? "",
      adres: drafts[row.id]?.address ?? row.address ?? "",
      not: drafts[row.id]?.note ?? row.delivery?.note ?? "",
    }));

    const columns: ExportColumn<ExportTransportRow>[] = [
      { key: "musteri", label: "Müşteri" },
      { key: "telefon", label: "Telefon" },
      { key: "kupeNo", label: "Küpe No" },
      { key: "siparisNo", label: "Sipariş No" },
      { key: "hisse", label: "Hisse" },
      { key: "tur", label: "Tür" },
      { key: "finans", label: "Finans" },
      { key: "hayvanDurumu", label: "Hayvan Durumu" },
      { key: "tasimaDurumu", label: "Taşıma Durumu" },
      { key: "tasiyici", label: "Taşıyıcı" },
      { key: "adres", label: "Adres" },
      { key: "not", label: "Not" },
    ];

    exportRowsToCsv(
      `tasima-${new Date().toISOString().slice(0, 10)}`,
      rows,
      columns
    );
  }

  function clearFilters() {
    setFilters(EMPTY_FILTERS);
  }

  return (
    <div className="min-h-screen bg-farm-cream font-body">
      <div className="max-w-[1700px] mx-auto px-3 sm:px-5 py-4 space-y-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="font-heading font-bold text-base text-farm-brown">
              Taşıma
            </h1>
            <p className="font-body text-xs text-farm-muted">
              Sadece taşıma işaretli hisseler görünür. Kesim yapılmış olması şart değildir.
            </p>
          </div>

          <button
            type="button"
            onClick={exportCurrent}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-full border border-farm-border bg-white text-farm-brown text-xs font-semibold hover:border-farm-earth/40"
          >
            <i className="ri-file-excel-2-line text-base" />
            Excel Çıktısı
          </button>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
          <div className="bg-farm-warm border border-farm-border rounded-farm px-3 py-2.5">
            <p className="text-[10px] text-farm-muted">Toplam</p>
            <p className="text-sm font-bold text-farm-brown mt-1">
              {summary.toplam}
            </p>
          </div>
          <div className="bg-farm-warm border border-farm-border rounded-farm px-3 py-2.5">
            <p className="text-[10px] text-farm-muted">Atanmadı</p>
            <p className="text-sm font-bold text-red-600 mt-1">
              {summary.atanmadi}
            </p>
          </div>
          <div className="bg-farm-warm border border-farm-border rounded-farm px-3 py-2.5">
            <p className="text-[10px] text-farm-muted">Hazır</p>
            <p className="text-sm font-bold text-farm-earth mt-1">
              {summary.hazir}
            </p>
          </div>
          <div className="bg-farm-warm border border-farm-border rounded-farm px-3 py-2.5">
            <p className="text-[10px] text-farm-muted">Yolda</p>
            <p className="text-sm font-bold text-farm-gold mt-1">
              {summary.yolda}
            </p>
          </div>
          <div className="bg-farm-warm border border-farm-border rounded-farm px-3 py-2.5">
            <p className="text-[10px] text-farm-muted">Teslim</p>
            <p className="text-sm font-bold text-farm-green mt-1">
              {summary.teslim}
            </p>
          </div>
        </div>

        <div className="bg-farm-warm border border-farm-border rounded-farm p-3 space-y-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-2">
            <input
              type="text"
              value={filters.customer}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, customer: e.target.value }))
              }
              placeholder="Müşteri ara..."
              className="w-full px-3 py-2 rounded-md border border-farm-border bg-farm-cream text-xs text-farm-brown outline-none"
            />

            <input
              type="text"
              value={filters.phone}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, phone: e.target.value }))
              }
              placeholder="Telefon ara..."
              className="w-full px-3 py-2 rounded-md border border-farm-border bg-farm-cream text-xs text-farm-brown outline-none"
            />

            <input
              type="text"
              value={filters.kupeNo}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, kupeNo: e.target.value }))
              }
              placeholder="Küpe No ara..."
              className="w-full px-3 py-2 rounded-md border border-farm-border bg-farm-cream text-xs text-farm-brown outline-none"
            />

            <input
              type="text"
              value={filters.siparisNo}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, siparisNo: e.target.value }))
              }
              placeholder="Sipariş No ara..."
              className="w-full px-3 py-2 rounded-md border border-farm-border bg-farm-cream text-xs text-farm-brown outline-none"
            />

            <select
              value={filters.status}
              onChange={(e) =>
                setFilters((prev) => ({
                  ...prev,
                  status: e.target.value as StatusFilter,
                }))
              }
              className="w-full px-3 py-2 rounded-md border border-farm-border bg-farm-cream text-xs text-farm-brown outline-none"
            >
              <option value="Tümü">Durum: Tümü</option>
              <option value="Atanmadı">Atanmadı</option>
              <option value="Hazır">Hazır</option>
              <option value="Taşıma Halinde">Taşıma Halinde</option>
              <option value="Teslim Edildi">Teslim Edildi</option>
            </select>
          </div>

          <div className="flex justify-end">
            <button
              type="button"
              onClick={clearFilters}
              className="text-[11px] text-farm-earth hover:text-farm-brown font-medium"
            >
              Filtreleri Temizle
            </button>
          </div>
        </div>

        {filteredRows.length === 0 ? (
          <div className="bg-farm-warm border border-farm-border rounded-farm py-12 text-center">
            <div className="w-10 h-10 flex items-center justify-center mx-auto text-farm-muted mb-2">
              <i className="ri-truck-line text-3xl" />
            </div>
            <p className="text-sm text-farm-muted">Taşıma kaydı bulunamadı</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredRows.map((row) => {
              const draft = drafts[row.id] ?? buildDraft(row);
              const status = getDisplayStatus(row);

              return (
                <div
                  key={row.id}
                  className="bg-farm-warm border border-farm-border rounded-farm p-3"
                >
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-bold text-farm-brown truncate">
                          {row.customerName}
                        </p>

                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] font-semibold ${statusBadge(
                            status
                          )}`}
                        >
                          {status}
                        </span>

                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] font-semibold ${financeBadge(
                            row.kalan
                          )}`}
                        >
                          {row.kalan > 0 ? `Borçlu ${fmt(row.kalan)}` : "Finans Tamam"}
                        </span>
                      </div>

                      <div className="mt-1 flex items-center gap-2 flex-wrap text-[11px] text-farm-muted">
                        <span className="font-semibold text-farm-brown">
                          Küpe: {row.kupeNo}
                        </span>
                        <span>•</span>
                        <span className="font-semibold text-farm-brown">
                          Sipariş: {row.siparisNo}
                        </span>
                        <span>•</span>
                        <span>{row.shareNo}</span>
                        <span>•</span>
                        <span>x{row.hisseSayisi}</span>
                        <span>•</span>
                        <span>{row.tur}</span>
                        <span>•</span>
                        <span>{row.transportLabel}</span>
                      </div>

                      <div className="mt-1 flex items-center gap-2 flex-wrap text-[11px] text-farm-muted">
                        <a
                          href={row.customerPhone ? `tel:${row.customerPhone}` : undefined}
                          className="text-farm-earth font-semibold"
                        >
                          {row.customerPhone || "Telefon yok"}
                        </a>
                        <span>•</span>
                        <span>Hisse Durumu: {row.saleStatus}</span>
                        <span>•</span>
                        <span>Hayvan Durumu: {row.animalStatus}</span>
                        <span>•</span>
                        <span>Toplam: {fmt(row.total)}</span>
                      </div>
                    </div>

                    <div className="text-[11px] text-farm-muted whitespace-nowrap">
                      Taşıyıcı:{" "}
                      <span className="font-semibold text-farm-brown">
                        {row.delivery?.carrierName ?? "—"}
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 xl:grid-cols-[1.2fr_180px_1fr_auto] gap-2 mt-3">
                    <input
                      type="text"
                      value={draft.address}
                      onChange={(e) =>
                        updateDraft(row.id, { address: e.target.value })
                      }
                      placeholder="Adres..."
                      className="w-full h-10 px-3 rounded-xl border border-farm-border bg-white text-sm outline-none"
                    />

                    <select
                      value={draft.carrierUserId}
                      onChange={(e) =>
                        updateDraft(row.id, { carrierUserId: e.target.value })
                      }
                      className="w-full h-10 px-3 rounded-xl border border-farm-border bg-white text-sm outline-none"
                    >
                      <option value="">Taşıyıcı seç</option>
                      {carrierUsers.map((carrier) => (
                        <option key={carrier.id} value={carrier.id}>
                          {carrier.adSoyad}
                        </option>
                      ))}
                    </select>

                    <input
                      type="text"
                      value={draft.note}
                      onChange={(e) =>
                        updateDraft(row.id, { note: e.target.value })
                      }
                      placeholder="Not..."
                      className="w-full h-10 px-3 rounded-xl border border-farm-border bg-white text-sm outline-none"
                    />

                    <div className="flex gap-2 flex-wrap">
                      <button
                        type="button"
                        onClick={() => saveAssignment(row)}
                        className="h-10 px-4 rounded-xl bg-farm-earth text-white text-xs font-semibold hover:bg-farm-brown"
                      >
                        Kaydet
                      </button>

                      {row.delivery && (
                        <button
                          type="button"
                          onClick={() => clearAssignment(row)}
                          className="h-10 px-4 rounded-xl border border-red-200 bg-red-50 text-red-600 text-xs font-semibold hover:bg-red-100"
                        >
                          Atamayı Sil
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 flex-wrap mt-3">
                    {(["Hazır", "Taşıma Halinde", "Teslim Edildi"] as const).map(
                      (target) => (
                        <button
                          key={target}
                          type="button"
                          onClick={() => setStatus(row, target)}
                          className={`h-9 px-3 rounded-full border text-[11px] font-semibold ${
                            status === target
                              ? target === "Teslim Edildi"
                                ? "bg-farm-green text-white border-farm-green"
                                : target === "Taşıma Halinde"
                                ? "bg-farm-gold text-white border-farm-gold"
                                : "bg-farm-earth text-white border-farm-earth"
                              : "bg-white text-farm-brown border-farm-border hover:border-farm-earth/40"
                          }`}
                        >
                          {target}
                        </button>
                      )
                    )}

                    {row.delivery?.carrierUserId && (
                      <span className="text-[11px] text-farm-muted ml-auto">
                        {carrierMap.get(Number(row.delivery.carrierUserId)) ?? ""}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}